import { useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useUserProjectRole } from 'portal/lib/hooks'
import { eventsCountLoad, eventsLoad } from 'portal/store/events/actions'

export const useInfractionEventsLoader = (
  startDate,
  endDate,
  moduleName,
  showDismissed = 'false',
  tableFilters = {},
  pageSize = 100
) => {
  const dispatch = useDispatch()
  const { accountId, projectId } = useParams()

  const { isReadOnly } = useUserProjectRole(accountId, projectId)

  const reload = useCallback(() => {
    const filterSourceTypes = ['Infraction']
    const filter = {
      startDate: startDate,
      endDate: endDate,
      sourceTypes: filterSourceTypes,
      submissionErrors: moduleName
        ? 'ExcludeSubmissionErrors'
        : 'SubmissionErrorsOnly',
      ...(showDismissed === 'false' ? { statuses: ['Open'] } : {}),
      moduleName: moduleName ? moduleName : undefined
    }

    // Combined Filters
    const infractionEventFilters = {
      ...filter,
      ...tableFilters
    }

    // If it exists, reformat the moduleName filter to be a single string
    if (infractionEventFilters.moduleName) {
      if (Array.isArray(infractionEventFilters.moduleName)) {
        // if only one moduleName use that one, if multiple, remove the filter
        if (infractionEventFilters.moduleName.length > 1) {
          delete infractionEventFilters.moduleName
        } else {
          infractionEventFilters.moduleName =
            infractionEventFilters.moduleName[0]
        }
      }
    }

    dispatch(
      eventsLoad(
        accountId,
        projectId,
        moduleName,
        pageSize,
        null,
        [],
        infractionEventFilters
      )
    )

    dispatch(
      eventsCountLoad(accountId, projectId, moduleName, infractionEventFilters)
    )
  }, [
    startDate,
    endDate,
    showDismissed,
    tableFilters,
    dispatch,
    accountId,
    projectId,
    moduleName,
    pageSize
  ])

  useEffect(() => {
    reload()
  }, [reload])

  const {
    loading,
    error,
    items,
    continuationToken,
    loadingCount,
    errorCount,
    count
  } = useSelector((state: any) => {
    return state.events[moduleName ? moduleName : 'submission']
  })

  function totalAlertsCount() {
    if (loadingCount) {
      return '....'
    }
    if (errorCount !== null) {
      return 'Error fetching total alerts'
    }
    return count
  }

  const handlePageChange = useCallback(() => {
    if (continuationToken === null) {
      return
    }
    const filter = {
      startDate: startDate,
      endDate: endDate,
      sourceTypes: ['Infraction'],
      submissionErrors: moduleName
        ? 'ExcludeSubmissionErrors'
        : 'SubmissionErrorsOnly',
      ...(showDismissed === 'false' ? { statuses: ['Open'] } : {})
    }

    // Table Filters
    const infractionEventFilters = {
      ...filter,
      ...tableFilters
    }

    dispatch(
      eventsLoad(
        accountId,
        projectId,
        moduleName,
        pageSize,
        continuationToken,
        items,
        infractionEventFilters
      )
    )
  }, [
    dispatch,
    accountId,
    projectId,
    moduleName,
    pageSize,
    continuationToken,
    items,
    startDate,
    endDate,
    showDismissed,
    tableFilters
  ])

  const hasNextPage = continuationToken != null

  return {
    loading,
    error,
    items,
    count,
    reload,
    handlePageChange,
    hasNextPage,
    totalAlertsCount,
    readOnly: isReadOnly
  }
}
